import { QueryStatus } from '@tanstack/react-query';
import { useTranslations } from 'use-intl';
import { ReactNode, useEffect, useMemo, useState } from 'react';
import Icon, { Icons } from '@/components/elements/Icon';
import Table, { TableHeading } from '@/components/modules/Table';
import InfiniteScroll from '@/components/modules/InfiniteScroll';
import { TokenInterface } from '@/core/tokens';
import TokensTableItem from './TokensTableItem';

export interface TokensTableProps {
  status: QueryStatus;
  tokens?: TokenInterface[];
  header?: ReactNode;
}

interface TokenTableHeading extends TableHeading {
  accessor?: keyof TokenInterface;
}

const LIMIT = 10;

export const Tokens = ({ tokens = [], status, header }: TokensTableProps) => {
  const [visible, setVisible] = useState(LIMIT);
  const [sortAccessor, setSortAccessor] = useState<string | undefined>();
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');
  const t = useTranslations('Tokens');

  const sortedTokens = useMemo(() => {
    if (!sortAccessor) {
      return tokens;
    }

    return tokens?.slice().sort((a: any, b: any) => {
      let av = a[sortAccessor];
      let bv = b[sortAccessor];
      if (sortDir === 'desc') {
        [av, bv] = [bv, av];
      }
      return Number(av - bv);
    });
  }, [tokens, sortAccessor, sortDir]);

  const handleSort = (accessor: string, direction: 'asc' | 'desc') => {
    setSortAccessor(accessor);
    setSortDir(direction);
  };

  const visibleTokens = sortedTokens?.slice(0, visible);
  const showMorePools = visibleTokens.length < sortedTokens.length;

  useEffect(() => {
    setSortAccessor(undefined);
    setSortDir('asc');
  }, [tokens]);

  return (
    <Table
      header={header}
      onSort={handleSort}
      headings={
        [
          { label: t('table.#'), align: 'left' },
          { label: t('table.token'), align: 'left', className: 'w-full' },
          {
            label: t('table.apr'),
          },
          {
            label: t('table.tvl'),
            accessor: 'totalValueLocked',
          },
          {
            label: t('table.vol24'),
            accessor: 'dailyTotalVolume',
          },
          {
            label: t('table.vol7d'),
            accessor: 'weeklyTotalVolume',
          },
          {
            label: t('table.poolCount'),
            tooltip: 'total amount of liquidity pools for this token',
          },
        ] as TokenTableHeading[]
      }
    >
      {status === 'loading' && (
        <tr>
          <td colSpan={7} className="p-20 text-center text-gray-500">
            <Icon
              name={Icons.SPINNER_GAP}
              className="animate animate-spin inline-flex"
              size={48}
            />
            <p className="mt-4">{t('loading')}</p>
          </td>
        </tr>
      )}
      {status === 'success' && (
        <InfiniteScroll
          onLoadMore={() => setVisible((x) => x + LIMIT)}
          scrollable={showMorePools}
          loaderElement="tr"
          loaderClassName="table-row"
          loaderChildren={
            <td colSpan={7} className="p-3 text-center text-gray-500">
              <Icon
                name={Icons.SPINNER_GAP}
                className="animate animate-spin inline-flex"
                size={20}
              />
            </td>
          }
        >
          {sortedTokens?.map(
            ({ id, symbol, name, totalValueLocked, pools, vaultId }, i) => {
              return (
                <TokensTableItem
                  key={id}
                  vaultId={vaultId}
                  index={i}
                  id={id}
                  symbol={symbol}
                  name={name}
                  totalValueLocked={totalValueLocked}
                  pools={pools}
                />
              );
            }
          )}
        </InfiniteScroll>
      )}
    </Table>
  );
};

export default Tokens;
